import {
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    EventEmitter,
    HostListener,
    Input,
    OnChanges, OnDestroy,
    OnInit,
    Output,
    SimpleChanges,
    ViewChild
} from '@angular/core';
import {v4 as uuid} from 'uuid';
import * as d3 from 'd3';

import {isDefined} from '../../commons/commons';
import {Observable, Subject} from 'rxjs';

import {bisectCenter, least, pointer} from 'd3/dist/d3'; // workaround due to @types/d3 being old w.r.t. D3
import * as _ from 'lodash';
import {takeUntil} from 'rxjs/operators';

const DEFAULT_MARGIN = 20;
const DEFAULT_COLOR = 'black';
const DEFAULT_STROKE_WIDTH = 1.5;
const DEFAULT_TIME_FORMAT = '%d/%m %H:%M';
const DEFAULT_SPACE_BETWEEN_X_TICKS = 80;
const DEFAULT_SPACE_BETWEEN_Y_TICKS = 40;
const MAX_ZOOM_SCALE = 32;
const DEFAULT_X_AXIS = true;
const DEFAULT_Y_AXIS = false;
const DEFAULT_TOOLTIP_BORDER_COLOR = '#EEEEEE';
const DEFAULT_TOOLTIP_BACKGROUND_COLOR = 'white';
const DEFAULT_SELECTION_LINE_COLOR = '#BDBDBD';

const Y_OFFSET_FACTOR = 0.025; // 2.5%
const DASH_LENGTH = 4;
const DASH_SEPARATOR_LENGTH = 4;

export interface ChartMargins {
    top?: number;
    right?: number;
    bottom?: number;
    left?: number;
}

export interface LineStyle {
    color: string;
    strokeWidth?: number;
    dashed?: boolean;
    opacity?: number; // @PoweredByAi(date="2026-06-25", model="claude-opus-4-8") — line opacity (e.g. hand-loaded measures at 50%)
}

export interface ChartLine {
    data: [number, number][];
    style: LineStyle;
    name?: string;
    fallbackTimes?: Set<number>;
    subName?: string;
}

export interface TextNode {
    text: string;
    color?: string;
    bold?: boolean;
    italic?: boolean;
}

export interface SelectionInfo {
    selectedIndices: number[]; // corresponding to input ChartLine[] elements; `null` if outside line's x domain
    closestIndex: number; // index of the line whose (selected) y value is closest to pointer
    lines: ChartLine[]; // all input lines provided for convenience
}

interface MouseSelection {
    currPoint: [number, number];  // (coordinates) [where mouse pointer is]
    selectedPoints: [number, number][]; // (coordinates) [where closest points on chart are - painted circles]; an element is `null` if point is outside domain for the corresponding line
    selectionInfo: SelectionInfo;
}

@Component({
    selector: 'app-line-chart',
    templateUrl: './line-chart.component.html',
    styleUrls: ['./line-chart.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class LineChartComponent implements OnInit, OnChanges, OnDestroy {

    private destroyed = new Subject<void>();

    @Input() data: ChartLine[];
    @Input() margins: ChartMargins;
    @Input() timeFormat: string;
    @Input() xGrid: boolean;
    @Input() xAxis: boolean;
    @Input() yAxis: boolean;
    @Input() yGrid: boolean;
    @Input() gridColor: string;
    @Input() xTickSpace: number;
    @Input() yTickSpace: number;
    @Input() yTickFormatter: (value: number) => string;
    @Input() tooltipFormatter: (selectionInfo: SelectionInfo) => TextNode[][]; // external array means lines of text
    @Input() tooltipBorderColor: string;
    @Input() tooltipBackgroundColor: string;
    @Input() selectionLineColor: string;
    @Input() maxZoomScale: number;
    @Input() enableZoom: boolean;
    @Input() enableSelection: boolean;
    @Input() resizeTrigger: Observable<void>;
    @Output() selection: EventEmitter<[number, number]> = new EventEmitter<[number, number]>();

    @ViewChild('chart') private chartContainer: ElementRef;

    constructor() {
    }

    ngOnInit() {
        setTimeout(() => {
            if (this.data) {
                this.destroyChart();
                this.createChart();
            }
        }, 0); // delayed, so that CSS styles (see "width: 100%") will have been computed
        if (this.resizeTrigger) {
            // re-create chart upon external resizing (may not be caught by window's resizing host listener)
            this.resizeTrigger.pipe(
                takeUntil(this.destroyed)
            ).subscribe(() => {
                this.destroyChart();
                this.createChart();
            });
        }
    }

    ngOnChanges(changes: SimpleChanges) {
        // if not first change and current and previous value are different, rebuild chart
        if (Object.getOwnPropertyNames(changes).find(p => !changes[p].isFirstChange() && JSON.stringify(changes[p].currentValue) !== JSON.stringify(changes[p].previousValue))) {
            this.destroyChart();
            this.createChart();
        }
    }

    ngOnDestroy() {
        this.destroyed.next();
        this.destroyed.complete();
    }

    // noinspection JSUnusedLocalSymbols (see https://stackoverflow.com/questions/50405930/aot-angular-6-directive-somecomponent-expected-0-arguments-but-got-1-for)
    @HostListener('window:resize', ['$event'])
    onResize(event) {
        // destroy and rebuild chart upon resizing
        this.destroyChart();
        this.createChart();
    }

    private createChart() {
        const element = this.chartContainer.nativeElement;

        const margins = {
            top: this.margins && isDefined(this.margins.top) ? this.margins.top : DEFAULT_MARGIN,
            right: this.margins && isDefined(this.margins.right) ? this.margins.right : DEFAULT_MARGIN,
            bottom: this.margins && isDefined(this.margins.bottom) ? this.margins.bottom : DEFAULT_MARGIN,
            left: this.margins && isDefined(this.margins.left) ? this.margins.left : DEFAULT_MARGIN
        };

        const width = element.offsetWidth - margins.left - margins.right;
        const height = element.offsetHeight - margins.top - margins.bottom;

        // quick fix for "transients"
        if (width - margins.left - margins.right < 0 || height - margins.top - margins.bottom < 0) {
            return;
        }

        // basic SVG container
        const svg = d3.select(element)
            .append('svg')
            .attr('width', element.offsetWidth)
            .attr('height', element.offsetHeight);

        const flatData = _.flatten(this.data.map(line => line.data));
        const xDomains = this.data.map(line => d3.extent(line.data, d => d[0]));

        // time axis
        const showXAxis = isDefined(this.xAxis) ? this.xAxis : DEFAULT_X_AXIS;
        const timeFormat = this.timeFormat || DEFAULT_TIME_FORMAT;
        const xTickSpace = isDefined(this.xTickSpace) ? this.xTickSpace : DEFAULT_SPACE_BETWEEN_X_TICKS;
        // @PoweredByAi(date="2026-06-29", model="claude-opus-4-8") — use a local-time scale so ticks land on
        // local boundaries: d3.timeFormat (below) renders in local time, so d3.scaleUtc placed ticks on UTC
        // instants (e.g. 00:00 UTC) that were then printed an hour off (01:00 CET). scaleTime keeps both consistent.
        const xScale = d3.scaleTime()
            .domain(d3.extent(flatData, d => d[0]))
            .range([margins.left, width - margins.right]);
        const xAxis = (g, x) => g
            .attr('transform', `translate(0,${height - margins.bottom})`)
            .call(d3.axisBottom(x)
                .tickFormat(d3.timeFormat(timeFormat))
                .tickSizeOuter(0)
                .ticks(Math.floor(width / xTickSpace)))
            .call(g => !showXAxis ? g.select('.domain').remove() : g)
            .selectAll('text')
            .style('text-anchor', 'end')
            // .attr('dx', '-.8em')
            // .attr('dy', '.15em')
            .attr('transform', 'rotate(-65)');

        // value axis
        const showYAxis = isDefined(this.yAxis) ? this.yAxis : DEFAULT_Y_AXIS;
        const yTickSpace = isDefined(this.yTickSpace) ? this.yTickSpace : DEFAULT_SPACE_BETWEEN_Y_TICKS;
        const yMinMax = d3.extent(flatData, d => d[1]);
        const yOffset = (yMinMax[1] - yMinMax[0]) * Y_OFFSET_FACTOR;
        const yScale = d3.scaleLinear()
            .domain([yMinMax[0] - yOffset, yMinMax[1] + yOffset])
            .range([height - margins.bottom, margins.top]);
        const yAxis = g => g
            .attr('transform', `translate(${margins.left},0)`)
            .call(d3.axisLeft(yScale)
                .tickFormat(this.yTickFormatter || (y => `${y}`))
                .tickSizeOuter(0)
                .ticks(Math.floor(height / yTickSpace)))
            .call(g => !showYAxis ? g.select('.domain').remove() : g);

        // actual plot line
        const line = x => d3.line()
            .defined(d => isDefined(d[1]) && !isNaN(d[1]))
            .x(d => x(d[0]))
            .y(d => yScale(d[1]));

        // zoom facility
        const zoom = !!this.enableZoom ?
            d3.zoom()
                .scaleExtent([1, isDefined(this.maxZoomScale) ? this.maxZoomScale : MAX_ZOOM_SCALE])
                .extent([[margins.left, 0], [width - margins.right, height]])
                .translateExtent([[margins.left, -Infinity], [width - margins.right, Infinity]])
                .filter(zoomFilter)
                .on('zoom', zoomed) :
            null;

        // brushing for selection
        const brush = !!this.enableSelection ?
            d3.brushX()
                .extent([[margins.left, margins.top], [width - margins.right, height - margins.bottom]])
                .on('start', selectionStart)
                .on('brush', selecting)
                .on('end', selectionEnd) :
            null;

        // clipping path (to avoid rendering anything outside of this area while zooming)
        const [clipId, clip] = mkClip();
        svg.append('clipPath')
            .attr('id', clipId)
            .append('rect')
            .attr('x', margins.left)
            .attr('y', margins.top)
            .attr('width', width - margins.left - margins.right)
            .attr('height', height - margins.top - margins.bottom);

        // populate chart with axes, (optional) grids and line
        const gx = svg.append('g')
            .call(xAxis, xScale);
        svg.append('g')
            .call(yAxis);
        if (!!this.xGrid) {
            const xAxisGrid = d3.axisBottom(xScale)
                .tickSize(-(height - margins.bottom - margins.top))
                .tickFormat(() => '')
                .ticks(width / xTickSpace);
            svg.append('g')
                .attr('class', 'x axis')
                .attr('transform', 'translate(0,' + (height - margins.bottom) + ')')
                .style('stroke-dasharray', ('3,3'))
                .call(xAxisGrid)
                .call(g => g.select('.domain').remove())
                .call(g => g.selectAll('.tick line')
                    .style('stroke', this.gridColor || DEFAULT_COLOR));
        }
        if (!!this.yGrid) {
            const yAxisGrid = d3.axisLeft(yScale)
                .tickSize(-(width - margins.right - margins.left))
                .tickFormat(() => '')
                .ticks(height / yTickSpace);
            svg.append('g')
                .attr('class', 'y axis')
                .attr('transform', 'translate(' + margins.left + ',0)')
                .style('stroke-dasharray', ('3,3'))
                .call(yAxisGrid)
                .call(g => g.select('.domain').remove())
                .call(g => g.selectAll('.tick line')
                    .style('stroke', this.gridColor || DEFAULT_COLOR))
                .call(g => g.selectAll('.tick:first-of-type line').remove());
        }

        const path = svg.append('g')
            .attr('clip-path', clip)
            .attr('fill', 'none')
            .attr('stroke-linejoin', 'round')
            .attr('stroke-linecap', 'round')
            .selectAll('path')
            .data(this.data)
            .join('path')
            .style('mix-blend-mode', 'multiply')
            .attr('d', d => line(xScale)(d.data))
            .attr('stroke', d => d.style.color || DEFAULT_COLOR)
            .attr('stroke-dasharray', mkDashArray)
            .attr('stroke-width', d => d.style.strokeWidth || DEFAULT_STROKE_WIDTH)
            // @PoweredByAi(date="2026-06-25", model="claude-opus-4-8")
            .attr('stroke-opacity', d => isDefined(d.style.opacity) ? d.style.opacity : 1);

        //  stroke dash array
        // see: https://wiredcraft.com/blog/dashed-line-segmentation-d3js/
        //      https://jsfiddle.net/epiph0ne/m1eLrmkb/
        function mkDashArray(chartLine: ChartLine): any {
            // @PoweredByAi(date="2026-06-25", model="claude-opus-4-8") — a fully-dashed line uses a constant
            // pattern: the per-segment length computation below assumes a single continuous path and produces
            // artifacts on lines broken by nulls (e.g. the statistical profile drawn only in gaps) (ETT-1161).
            if (chartLine.style?.dashed) {
                return `${DASH_LENGTH},${DASH_SEPARATOR_LENGTH}`;
            }
            if (!hasDashesPoints(chartLine)) {
                return null;
            }
            const dashedRanges = mkDashedRanges(chartLine);
            if (dashedRanges.length === 0) {
                return null;
            }
            // NOTE: `this` points to "path"
            const lengths = chartLine.data.map(d => getPathLengthAtX(this, (scaledX || xScale)(d[0])));
            const dashArray = buildDashArray(dashedRanges, lengths);
            return dashArray;
        }

        // add zooming
        if (zoom) {
            svg.call(zoom);
        }

        // add brushing
        if (brush) {
            svg.append('g')
                .attr('class', 'brush')
                .call(brush);
        }

        function zoomFilter(event) {
            switch (event.type) {
                case 'mousedown':
                    return event.button === 1;
                case 'wheel':
                    return event.button === 0;
                default:
                    return false;
            }
        }

        let scaledX = xScale;

        function zoomed(event) {
            // @ts-ignore
            scaledX = event.transform.rescaleX(xScale);
            path.attr('d', d => line(scaledX)(d.data))
                .attr('stroke-dasharray', mkDashArray);
            gx.call(xAxis, scaledX);
        }

        const selectionEmitter = this.selection;

        function selectionStart(event) {
        }

        function selecting(event) {
            if (!!event.selection) {
                tooltipCb.bind(this)(event);
            }
        }

        function selectionEnd(event) {
            const extent = event.selection;
            if (extent) {
                // emit selected value range within the x domain
                const minX = scaledX.invert(extent[0]).valueOf();
                const maxX = scaledX.invert(extent[1]).valueOf();
                selectionEmitter.emit([minX, maxX]);
                // remove brush area as soon as selection is finished
                svg.select('.brush')
                    .call(brush.move, null);
            }
        }

        // tooltip-related stuff

        const focus = svg.append('g')
            .style('display', null)
            .style('pointer-events', 'none');
        const selectionPoint = focus.selectAll('circle')
            .data(this.data)
            .join('circle');
        const selectionLine = focus.append('line');
        const tooltip = focus.append('g');

        const ttCallout = (g: d3.Selection<SVGElement, unknown, null, undefined>, ttText: TextNode[][], sel: MouseSelection) => {
            if (!ttText || !sel) {
                focus.style('display', 'none');
                return;
            }
            focus.style('display', null);

            selectionPoint.attr('display', (d, i) => !!sel.selectedPoints[i] && isDefined(sel.selectedPoints[i][0]) && isDefined(sel.selectedPoints[i][1]) ? null : 'none')
                .attr('r', 3)
                .attr('cx', (d, i) => sel.selectedPoints[i] && !isNaN(sel.selectedPoints[i][0]) ? sel.selectedPoints[i][0] : null)
                .attr('cy', (d, i) => sel.selectedPoints[i] && !isNaN(sel.selectedPoints[i][1]) ? sel.selectedPoints[i][1] : null)
                .attr('fill', this.tooltipBackgroundColor || DEFAULT_TOOLTIP_BACKGROUND_COLOR)
                .attr('stroke', (d, i) => d.style.color)
                .attr('stroke-width', d => d.style.strokeWidth || DEFAULT_STROKE_WIDTH)
                // @PoweredByAi(date="2026-06-25", model="claude-opus-4-8")
                .attr('stroke-opacity', d => isDefined(d.style.opacity) ? d.style.opacity : 1);
            // NOTE: we could, in principle, draw a circle around nearest point as well
            selectionLine.attr('x1', sel.currPoint[0])
                .attr('y1', height - margins.bottom)
                .attr('x2', sel.currPoint[0])
                .attr('y2', margins.top)
                .style('stroke-width', 1)
                .style('stroke', this.selectionLineColor || DEFAULT_SELECTION_LINE_COLOR)
                .style('fill', 'none');

            const path = g.selectAll('path')
                .data([null])
                .join('path')
                .attr('fill', this.tooltipBackgroundColor || DEFAULT_TOOLTIP_BACKGROUND_COLOR)
                .attr('stroke', this.tooltipBorderColor || DEFAULT_TOOLTIP_BORDER_COLOR);

            const lines = _.flatten(ttText.map((line, idx) => line.map(entry => ({...entry, lineIdx: idx}))));

            const text = g.selectAll('text')
                .data([null])
                .join('text')
                .call(txt => txt
                    .selectAll('tspan')
                    .data(lines)
                    .join('tspan')
                    .attr('x', (d, i) => (i === 0 || lines[i].lineIdx !== lines[i - 1].lineIdx) ? 0 : null)
                    .attr('y', (d, i) => `${d.lineIdx * 1.5}em`)
                    .attr('fill', d => d.color || DEFAULT_COLOR)
                    .style('font-weight', d => d.bold ? 'bold' : null)
                    .style('font-style', d => d.italic ? 'italic' : null)
                    .text(d => d.text));

            const {y, width: w, height: h} = (text.node() as SVGGraphicsElement).getBBox();

            let xOffset;
            if (sel.currPoint[0] + w > width - margins.right) {
                text.attr('transform', `translate(${-w},${15 - y})`);
                path.attr('d', `M${-w - 10},5H-5l5,0l5,0H${10}v${h + 20}h-${w + 20}z`);
                xOffset = -20;
            } else {
                text.attr('transform', `translate(${0},${15 - y})`);
                path.attr('d', `M${-10},5H-5l5,0l5,0H${w + 10}v${h + 20}h-${w + 20}z`);
                xOffset = +20;
            }

            // set tooltip's position
            g.attr('transform', `translate(${sel.currPoint[0] + xOffset},${margins.top + 20})`)
                .style('pointer-events', 'none')
                .style('font', '10px sans-serif');
        };

        const computeMouseSelection = ([mx, my]: number[]): MouseSelection => {
            const [xMin, xMax] = scaledX.range();
            const [yMax, yMin] = yScale.range();
            if (mx < xMin || mx > xMax || my < yMin || my > yMax) {
                return null;
            }
            const x = scaledX.invert(mx).valueOf();
            const y = yScale.invert(my);
            const selectedIdx = this.data.map((line, i) => bisectCenter(line.data.map(d => d[0]), x)) // indices of points with closest x(s) within multiple input lines
                .map((idx, i) => x >= xDomains[i][0] && x <= xDomains[i][1] ? idx : null); // "discard" indices of points outside respective domains
            return {
                currPoint: [mx, my],
                selectedPoints: selectedIdx.map((idx, i) => idx !== null ? [scaledX(this.data[i].data[idx][0]), yScale(this.data[i].data[idx][1])] : null),
                selectionInfo: {
                    selectedIndices: selectedIdx,
                    closestIndex: least(d3.range(0, selectedIdx.length, 1), i => selectedIdx[i] !== null ? Math.abs(this.data[i].data[selectedIdx[i]][1] - y) : Number.POSITIVE_INFINITY),
                    lines: component.data
                }
            };
        };

        const component = this;

        function tooltipCb(event) {
            const ms = computeMouseSelection(pointer(event, this));
            if (!ms) { // clear tooltip in case of pointer-outside or "no data"
                tooltip.call(ttCallout, null, null);
                return;
            }
            tooltip.call(ttCallout, component.tooltipFormatter(ms.selectionInfo), ms);
        }

        if (this.tooltipFormatter) {
            svg.on('mouseover', () => {
            }).on('mouseout', () => {
            }).on('touchmove mousemove', tooltipCb);
            svg.on('touchend mouseleave', () => {
                tooltip.call(ttCallout, null, null);
            });
        }
    }

    private destroyChart() {
        d3.select(this.chartContainer.nativeElement)
            .select('svg')
            .remove();
    }
}

function mkClip(): [string, string] {
    const clipId = uuid();
    // @ts-ignore
    return [clipId, `url(${new URL(`#${clipId}`, location)})`];
}

function mkDashedRanges(chartLine: ChartLine): { start: number, end: number }[] {
    if (chartLine.data.length === 0) {
        return null;
    }
    const hasOpenRange = (arr: { start: number, end: number }[]) => {
        const last = _.last(arr);
        return !!last && !isDefined(last.end);
    };
    const lastIndex = chartLine.data.length - 1;
    return _.reduce(chartLine.data, (acc, d, i: number) => {
        const isRangeStart = !hasOpenRange(acc) && isDashedPoint(chartLine, d, i);
        if (isRangeStart) {
            acc.push({start: i, end: undefined});
        }
        const isRangeEnd = hasOpenRange(acc) && (!isDashedPoint(chartLine, d, i) || i === lastIndex);
        if (isRangeEnd) {
            acc[acc.length - 1].end = i;
        }
        return acc;
    }, [] as { start: number, end: number }[]);
}

function hasDashesPoints(chartLine: ChartLine): boolean {
    return !!chartLine.fallbackTimes?.size;
}

function isDashedPoint(chartLine: ChartLine, d: [number, number], i: number): boolean {
    return !!chartLine.fallbackTimes?.has(d[0]);
}

function getPathLengthAtX(path: SVGPathElement, x: number): number {
    const EPSILON = 1;
    let point: DOMPoint;
    let target: number;
    let start: number = 0;
    let end: number = path.getTotalLength();
    while (true) {
        target = Math.floor((start + end) / 2);
        point = path.getPointAtLength(target);
        if (Math.abs(point.x - x) <= EPSILON) {
            break;
        }
        if ((target >= end || target <= start) && point.x !== x) {
            break;
        }
        if (point.x > x) {
            end = target;
        } else if (point.x < x) {
            start = target;
        } else {
            break;
        }
    }
    return target;
}

function buildDashArray(dashedRanges: { start: number, end: number }[], lengths: number[]): (number | string)[] {
    const dashArray = _.reduce(dashedRanges, (acc, {start, end}, i: number) => {
        const prevEnd = i === 0 ? 0 : dashedRanges[i - 1].end;
        const normalSegment = lengths[start] - lengths[prevEnd];
        const dashedSegment = getDashedSegment(lengths[end] - lengths[start]);
        return acc.concat([normalSegment, dashedSegment]);
    }, [] as (number | string)[]);
    // take into account a potential ending normal segment
    const endingNormalSegment = lengths[lengths.length - 1] - lengths[dashedRanges[dashedRanges.length - 1].end];
    if (endingNormalSegment > 0) {
        dashArray.push(endingNormalSegment, '0,0');
    }
    return dashArray;
}

function getDashedSegment(length: number): string {
    const totalDashLen = DASH_LENGTH + DASH_SEPARATOR_LENGTH;
    const dashCount = Math.floor(length / totalDashLen);
    return _.range(dashCount)
        .map(() => DASH_SEPARATOR_LENGTH + ',' + DASH_LENGTH)
        .concat((length - dashCount * totalDashLen).toString())
        .join(',');
}
